import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiArrowLeft, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { FaEnvelope, FaWhatsapp, FaClock, FaCheckCircle, FaMobileAlt } from 'react-icons/fa';
import LanguageSwitcher from '../../Components/LanguageSwitcher/LanguageSwitcher';
import zoomLogo from '../../assets/zoom-logo.svg';
import styles from './Support.module.css';
import { TEMP_CRM_PIVOT } from '../../config/tempCrmPivot';
import SEO from '../../Components/SEO/SEO';
import { STATIC_PAGE_META } from '../../seo/pageMeta';

const SUPPORT_EMAIL = 'hello@stylon.co';
const WHATSAPP_URL = 'https://wa.me/972533920842';

const FAQ_KEYS = ['q1', 'q2', 'q3', 'q4', 'q5'];

function FaqItem({ faqKey, pivot }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const qKey = pivot ? `tempPivot.support.${faqKey}Q` : `support.${faqKey}Q`;
  const aKey = pivot ? `tempPivot.support.${faqKey}A` : `support.${faqKey}A`;

  return (
    <div className={styles.faqItem}>
      <SEO {...STATIC_PAGE_META['support']} />
      <button
        className={styles.faqQuestion}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span>{t(qKey)}</span>
        {open ? <FiChevronUp className={styles.faqIcon} /> : <FiChevronDown className={styles.faqIcon} />}
      </button>
      {open && <p className={styles.faqAnswer}>{t(aKey)}</p>}
    </div>
  );
}

const Support = () => {
  const { t } = useTranslation();

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.topBar}>
          <Link to="/" className={styles.backButton} aria-label={t('legal.back')}>
            <FiArrowLeft /> {t('legal.back')}
          </Link>
          <LanguageSwitcher variant="light" />
        </div>

        <h1 className={styles.title}>{t('support.title')}</h1>
        <p className={styles.subtitle}>{t('support.subtitle')}</p>

        {/* Contact channels */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('support.contactTitle')}</h2>
          <div className={styles.contactCards}>
            <a href={`mailto:${SUPPORT_EMAIL}`} className={styles.contactCard}>
              <FaEnvelope className={styles.contactIcon} style={{ color: '#FE4E00' }} />
              <div className={styles.contactInfo}>
                <span className={styles.contactLabel}>{t('support.emailLabel')}</span>
                <span className={styles.contactValue}>{SUPPORT_EMAIL}</span>
              </div>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className={styles.contactCard}>
              <FaWhatsapp className={styles.contactIcon} style={{ color: '#25D366' }} />
              <div className={styles.contactInfo}>
                <span className={styles.contactLabel}>{t('support.whatsappLabel')}</span>
                <span className={styles.contactValue}>{t('support.whatsappValue')}</span>
              </div>
            </a>
          </div>
        </section>

        {/* Hours & SLA */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('support.hoursTitle')}</h2>
          <div className={styles.infoGrid}>
            <div className={styles.infoCard}>
              <FaClock className={styles.infoIcon} style={{ color: '#FE4E00' }} />
              <div>
                <p className={styles.infoLabel}>{t('support.hoursLabel')}</p>
                <p className={styles.infoValue}>{t('support.hoursValue')}</p>
              </div>
            </div>
            <div className={styles.infoCard}>
              <FaCheckCircle className={styles.infoIcon} style={{ color: '#FE4E00' }} />
              <div>
                <p className={styles.infoLabel}>{t('support.slaLabel')}</p>
                <p className={styles.infoValue}>{t('support.slaValue')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Troubleshooting Q&A */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('support.troubleshootingTitle')}</h2>
          <p className={styles.sectionBody}>{t('support.troubleshootingSubtitle')}</p>
          <div className={styles.faqList}>
            {FAQ_KEYS.map((key) => (
              <FaqItem key={key} faqKey={key} pivot={TEMP_CRM_PIVOT} />
            ))}
          </div>
        </section>

        {/* Integrations */}
        <section className={`${styles.section} ${styles.lastSection}`}>
          <h2 className={styles.sectionTitle}>{t('support.integrationsTitle')}</h2>
          <p className={styles.sectionBody}>{t('support.integrationsBody')}</p>
          <Link to="/setup-app" className={styles.integrationCard}>
            <FaMobileAlt className={styles.integrationIcon} style={{ color: '#FE4E00' }} />
            <div className={styles.integrationText}>
              <span className={styles.integrationName}>{t('support.setupAppName')}</span>
              <span className={styles.integrationDesc}>{t('support.setupAppDesc')}</span>
            </div>
            <FiChevronDown className={styles.integrationArrow} style={{ transform: 'rotate(-90deg)' }} />
          </Link>
          <Link to="/zoom-integration" className={styles.integrationCard}>
            <img
              src={zoomLogo}
              alt="Zoom"
              className={styles.integrationLogo}
            />
            <div className={styles.integrationText}>
              <span className={styles.integrationName}>{t('support.zoomIntegrationName')}</span>
              <span className={styles.integrationDesc}>{t('support.zoomIntegrationDesc')}</span>
            </div>
            <FiChevronDown className={styles.integrationArrow} style={{ transform: 'rotate(-90deg)' }} />
          </Link>
        </section>
      </div>
    </div>
  );
};

export default Support;
